import { useMemo, useState } from 'react';
import { gql, useQuery } from 'urql';

export const GET_PARTICIPANTS_BY_EVENT_ID_QUERY = gql`
  query GetParticipantsByEventId(
    $eventId: Int!
    $source: ValidatedSource!
    $searchString: String!
    $guestFilter: GuestFilter
  ) {
    getParticipantsByEventId(
      eventId: $eventId
      source: $source
      searchString: $searchString
      guestFilter: $guestFilter
    ) {
      ticketId
      id
      firstName
      lastName
      surname
      email
      phone
      position
    }
  }
`;

export interface ParticipantByEventId {
  ticketId: number;
  id: number;
  firstName: string;
  lastName: string;
  surname: string;
  email: string;
  phone: string | null;
  position: string | null;
}

interface GetParticipantsByEventIdResponse {
  getParticipantsByEventId: ParticipantByEventId[];
}

interface GetParticipantsByEventIdVariables {
  eventId: number;
  source: string;
}

export const useGetParticipantsByEventId = (
  participantsByEventIdVariables: GetParticipantsByEventIdVariables,
  options?: { fetchFresh?: boolean }
) => {
  const [searchString, setSearchString] = useState<string>('');
  const [guestFilter, setGuestFilter] = useState<string | null>(null);
  const { eventId, source } = participantsByEventIdVariables;

  const [{ data, fetching, error }, reexecuteQuery] = useQuery<GetParticipantsByEventIdResponse>({
    query: GET_PARTICIPANTS_BY_EVENT_ID_QUERY,
    variables: { eventId, source, searchString, guestFilter },
    requestPolicy: options?.fetchFresh ? 'network-only' : 'cache-first',
  });

  const handleSearchChange = (value: string) => {
    setSearchString(value);
  };

  const handleGuestFilterChange = (value: string | null) => {
    setGuestFilter(value);
  };

  const refetch = () => {
    reexecuteQuery({ requestPolicy: 'network-only' });
  };

  const participants = useMemo(() => data?.getParticipantsByEventId, [data]);

  return { participants, fetching, error, refetch, handleSearchChange, handleGuestFilterChange };
};
